import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Edit, KeyRound, Loader2, LogIn, Mail, PackageCheck, ShieldAlert, Trash2, UserCheck } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/billing/StatusBadge";
import { UsageLimitBar } from "@/components/billing/UsageLimitBar";
import { ALL_ADMIN_ROLES, adminUserAction, fetchManagedUsers, getInitials, isCurrentUserSuperAdmin, isCurrentUserTeamManager } from "@/lib/adminUsers";
import { db, formatDate, money } from "@/lib/billing";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

async function fetchUserDetail(userId: string) {
  const safeSelect = async (query: PromiseLike<any>, fallback: any = []) => {
    const result = await query;
    if (result.error) {
      console.warn("Optional user detail data source unavailable", result.error);
      return fallback;
    }
    return result.data ?? fallback;
  };

  const managed = await fetchManagedUsers();
  const users = managed.users;
  const [
    packages,
    subscriptions,
    usage,
    invoices,
    websites,
    articles,
    media,
    activity,
    notes,
    billingEvents,
    impersonation,
  ] = await Promise.all([
    managed.can_manage_all ? safeSelect(db.from("packages").select("*").order("sort_order")) : [],
    safeSelect(db.from("user_subscriptions").select("*, packages(*)").eq("user_id", userId).order("created_at", { ascending: false })),
    safeSelect(db.from("usage_counters").select("*").eq("user_id", userId).order("period_start", { ascending: false }).limit(1).maybeSingle(), null),
    safeSelect(db.from("invoices").select("*, packages(name)").eq("user_id", userId).order("created_at", { ascending: false })),
    safeSelect(db.from("websites").select("id, name, domain, status, created_at, article_count").eq("owner_id", userId).order("created_at", { ascending: false })),
    safeSelect(db.from("articles").select("id, title, status, created_at, published_at").eq("author_id", userId).order("created_at", { ascending: false })),
    safeSelect(db.from("media_assets").select("*").eq("user_id", userId).order("created_at", { ascending: false }).limit(100)),
    safeSelect(db.from("user_activity_logs").select("*").or(`target_user_id.eq.${userId},actor_user_id.eq.${userId}`).order("created_at", { ascending: false }).limit(200)),
    safeSelect(db.from("admin_user_notes").select("*").eq("target_user_id", userId).order("created_at", { ascending: false })),
    safeSelect(db.from("billing_events").select("*").eq("user_id", userId).order("created_at", { ascending: false }).limit(50)),
    safeSelect(db.from("impersonation_sessions").select("*").or(`target_user_id.eq.${userId},admin_user_id.eq.${userId}`).order("started_at", { ascending: false }).limit(50)),
  ]);

  return {
    user: users.find((item) => item.id === userId) ?? null,
    packages,
    subscriptions,
    subscription: subscriptions?.[0] ?? null,
    usage,
    invoices,
    websites,
    articles,
    media,
    activity,
    notes,
    billingEvents,
    impersonation,
    canManageAll: managed.can_manage_all,
  };
}

export default function UserDetailPage() {
  const { id } = useParams();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [note, setNote] = useState("");
  const [packageId, setPackageId] = useState("");
  const [trialEnd, setTrialEnd] = useState("");
  const [overrideJson, setOverrideJson] = useState("{}");
  const [impersonationReason, setImpersonationReason] = useState("");
  const [passwordResetEmail, setPasswordResetEmail] = useState("");
  const [removeOpen, setRemoveOpen] = useState(false);
  const [forceDeleteOpen, setForceDeleteOpen] = useState(false);
  const [forceDeletePhrase, setForceDeletePhrase] = useState("");

  const { data: isAdmin, isLoading: adminLoading } = useQuery({ queryKey: ["can-manage-team", currentUser?.id], queryFn: () => isCurrentUserTeamManager(currentUser?.id), enabled: !!currentUser });
  const { data: isSuperAdmin } = useQuery({ queryKey: ["is-super-admin", currentUser?.id], queryFn: () => isCurrentUserSuperAdmin(currentUser?.id), enabled: !!currentUser });
  const { data, isLoading } = useQuery({ queryKey: ["admin-user-detail", id], queryFn: () => fetchUserDetail(id!), enabled: !!isAdmin && !!id });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-user-detail", id] });
    queryClient.invalidateQueries({ queryKey: ["admin-users"] });
  };

  const action = useMutation({
    mutationFn: ({ name, payload }: { name: string; payload: Record<string, unknown> }) => adminUserAction(name, { user_id: id, ...payload }),
    onSuccess: (_data, variables) => {
      toast.success("Action completed");
      if (variables.name === "start_impersonation") {
        const target = data?.user;
        localStorage.setItem("delapresse_impersonation", JSON.stringify({
          session_id: (_data as any)?.session_id,
          target_user_id: id,
          target_name: target?.full_name ?? target?.email ?? id,
          admin_user_id: currentUser?.id,
        }));
        window.dispatchEvent(new Event("storage"));
        navigate("/dashboard");
      }
      refresh();
    },
    onError: (err: any) => toast.error(err?.message ?? "Action failed"),
  });

  const sendVerification = useMutation({
    mutationFn: async () => {
      if (!data?.user?.email) throw new Error("User has no email address");
      const { error } = await supabase.functions.invoke("send-verification-code", { body: { email: data.user.email } });
      if (error) throw error;
    },
    onSuccess: () => toast.success("Verification email sent"),
    onError: (err: any) => toast.error(err?.message ?? "Could not send verification"),
  });

  const sendPasswordReset = useMutation({
    mutationFn: async () => {
      const resetEmail = passwordResetEmail.trim() || data?.user?.email || "";
      const { error } = await supabase.functions.invoke("send-password-reset", {
        body: { user_id: id, email: resetEmail, redirect_to: `${window.location.origin}/reset-password` },
      });
      if (!error) return;

      let functionMessage = error.message;
      const context = (error as any).context;
      if (context instanceof Response) {
        const body = await context.clone().json().catch(() => null);
        functionMessage = body?.error ?? functionMessage;
      }

      if (!resetEmail) {
        throw new Error(`${functionMessage}. Enter the user's email address in the Security tab and try again.`);
      }

      console.warn("send-password-reset Edge Function failed; falling back to Supabase resetPasswordForEmail", functionMessage);
      const { error: fallbackError } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (fallbackError) {
        throw new Error(`${functionMessage}. Fallback reset also failed: ${fallbackError.message}`);
      }
    },
    onSuccess: () => toast.success("Password reset email sent"),
    onError: (err: any) => toast.error(err?.message ?? "Could not send password reset"),
  });

  const removalImpact = useQuery({
    queryKey: ["team-user-removal-impact", id],
    queryFn: () => adminUserAction("analyze_user_removal", { user_id: id }),
    enabled: removeOpen && !!id,
  });

  const removeUser = useMutation({
    mutationFn: (userId: string) => adminUserAction("remove_user", {
      action: "remove_user",
      user_id: userId,
      company_id: data?.user?.company_id,
      deletion_mode: "remove_from_company",
      reason: "Removed from company/team from user detail",
    }),
    onSuccess: async (result: any) => {
      const summary = result?.summary;
      toast.success(summary
        ? `User removed from company/team. Removed ${summary.deleted_records_count ?? 0} membership records.`
        : "User removed from company/team");
      await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      await queryClient.invalidateQueries({ queryKey: ["admin-users-managed"] });
      navigate("/team");
    },
    onError: (err: any) => toast.error(err?.message ?? "Could not remove user"),
  });

  const forceDeleteUser = useMutation({
    mutationFn: ({ userId, confirmation }: { userId: string; confirmation: string }) => adminUserAction("force_delete_user", {
      action: "force_delete_user",
      user_id: userId,
      deletion_mode: "full",
      confirmation,
      reason: "Deleted completely from user detail",
    }),
    onSuccess: async (result: any) => {
      const summary = result?.summary;
      const deleted = summary?.deleted_records_count ?? 0;
      const preserved = summary?.preserved_records_count ?? 0;
      toast.success(summary?.auth_account_deleted
        ? `User permanently deleted. Deleted ${deleted} records; detached ${summary?.detached_records_count ?? 0} references.`
        : `Full delete did not complete. Deleted ${deleted} records; preserved ${preserved} records.`);
      setForceDeleteOpen(false);
      setForceDeletePhrase("");
      await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      await queryClient.invalidateQueries({ queryKey: ["admin-users-managed"] });
      navigate("/team");
    },
    onError: (err: any) => toast.error(err?.message ?? "Could not delete user completely"),
  });

  const confirmSafeRemove = () => {
    if (!id) {
      toast.error("Missing target user id. Open the user detail page again and retry.");
      return;
    }
    removeUser.mutate(id);
  };

  const openForceDeleteConfirmation = () => {
    if (!id) {
      toast.error("Missing target user id. Open the user detail page again and retry.");
      return;
    }
    if (forceDeletePhrase.trim() !== "DELETE") {
      toast.error("Type DELETE to enable complete user deletion.");
      return;
    }
    setForceDeleteOpen(true);
  };

  const confirmForceDelete = () => {
    if (!id) {
      toast.error("Missing target user id. Open the user detail page again and retry.");
      return;
    }
    const confirmation = forceDeletePhrase.trim();
    if (confirmation !== "DELETE") {
      toast.error("Type DELETE to confirm complete user deletion.");
      return;
    }
    forceDeleteUser.mutate({ userId: id, confirmation });
  };

  const timeline = useMemo(() => {
    const activity = (data?.activity ?? []).map((item: any) => ({ ...item, source: "activity", at: item.created_at }));
    const billing = (data?.billingEvents ?? []).map((item: any) => ({
      id: `billing-${item.id}`,
      source: "billing",
      at: item.created_at,
      event_type: item.event_type,
      event_action: item.event_type,
      status: item.severity,
      message: item.message,
      metadata: item.metadata,
    }));
    const impersonation = (data?.impersonation ?? []).flatMap((item: any) => [
      { id: `imp-start-${item.id}`, source: "impersonation", at: item.started_at, event_type: "impersonation", event_action: "started", status: item.status, message: "Impersonation started", metadata: item },
      ...(item.ended_at ? [{ id: `imp-end-${item.id}`, source: "impersonation", at: item.ended_at, event_type: "impersonation", event_action: "ended", status: "ended", message: "Impersonation ended", metadata: item }] : []),
    ]);
    return [...activity, ...billing, ...impersonation].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  }, [data]);

  useEffect(() => {
    if (data?.user?.email && !passwordResetEmail) {
      setPasswordResetEmail(data.user.email);
    }
  }, [data?.user?.email, passwordResetEmail]);

  if (adminLoading || isLoading) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  if (!isAdmin) return <Navigate to="/team" replace />;
  if (!data?.user) return <Navigate to="/team" replace />;

  const target = data.user;
  const subscription = data.subscription;
  const pkg = subscription?.packages;
  const usage = data.usage ?? {};
  const canImpersonate = isSuperAdmin || !target.roles.some((role) => role === "admin" || role === "super_admin");
  const canManageAll = Boolean(data.canManageAll);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur-xl">
        <div>
          <h1 className="text-lg font-semibold text-foreground">User Detail</h1>
          <p className="text-xs text-muted-foreground">Complete administration center for this Delapresse account.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm"><Link to="/team"><ArrowLeft className="mr-2 h-4 w-4" />Users</Link></Button>
          <Button asChild variant="outline" size="sm"><Link to={`/team/users/${id}/edit`}><Edit className="mr-2 h-4 w-4" />Edit</Link></Button>
        </div>
      </header>

      <main className="space-y-6 p-6">
        <Card>
          <CardContent className="flex flex-col gap-5 p-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16"><AvatarImage src={target.avatar_url ?? undefined} /><AvatarFallback>{getInitials(target.full_name)}</AvatarFallback></Avatar>
              <div>
                <h2 className="text-xl font-semibold">{target.full_name ?? "Unnamed user"}</h2>
                <p className="text-sm text-muted-foreground">{target.email ?? "Email unavailable"} · {target.phone_number ?? "No phone"}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <StatusBadge status={target.account_status} />
                  <Badge variant={target.email_verified ? "default" : "secondary"}>{target.email_verified ? "Email verified" : "Email pending"}</Badge>
                  {target.roles.map((role) => <Badge key={role} variant="outline">{role.replaceAll("_", " ")}</Badge>)}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => sendVerification.mutate()} disabled={sendVerification.isPending}><Mail className="mr-2 h-4 w-4" />Resend verification</Button>
              <Button variant="outline" onClick={() => sendPasswordReset.mutate()} disabled={sendPasswordReset.isPending}><KeyRound className="mr-2 h-4 w-4" />Reset password</Button>
              <Button variant="outline" disabled={!canImpersonate || action.isPending} onClick={() => action.mutate({ name: "start_impersonation", payload: { reason: impersonationReason } })}><LogIn className="mr-2 h-4 w-4" />Login as user</Button>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="flex h-auto flex-wrap justify-start">
            {["overview", "roles", ...(canManageAll ? ["billing"] : []), "websites", "articles", "media", "timeline", "invoices", "notes", "security"].map((tab) => (
              <TabsTrigger key={tab} value={tab} className="capitalize">{tab === "billing" ? "Package & Billing" : tab === "roles" ? "Roles & Permissions" : tab}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview" className="grid gap-4 md:grid-cols-4">
            <Summary label="Created" value={formatDate(target.created_at)} />
            <Summary label="Last login" value={formatDate(target.last_login_at)} />
            <Summary label="Websites" value={target.websites_count} />
            <Summary label="Articles" value={target.articles_count} />
            <Summary label="Media assets" value={target.media_count} />
            <Summary label="Invoices" value={target.invoice_count} />
            <Summary label="Current package" value={target.package_name ?? "None"} />
            <Summary label="Payment status" value={target.payment_status ?? "None"} />
            <Summary label="Company" value={target.company_name ?? "None"} />
            <Summary label="Company role" value={target.company_role ?? "None"} />
            <Summary label="Country" value={target.country ?? "None"} />
            <Summary label="Language" value={target.preferred_language === "en" ? "English" : "Français"} />
            <Summary label="Address" value={target.address ?? "None"} />
            <Summary label="Permissions" value={target.permissions?.length ? `${target.permissions.length} grants` : "None"} />
          </TabsContent>

          <TabsContent value="roles">
            <Card><CardHeader><CardTitle className="text-base">Roles & Permissions</CardTitle></CardHeader><CardContent className="grid gap-3 md:grid-cols-3">
              {(canManageAll ? ALL_ADMIN_ROLES : []).map((role) => {
                const checked = target.roles.includes(role.value);
                return (
                  <label key={role.value} className="flex items-center justify-between rounded-md border border-border p-3 text-sm">
                    <span>{role.label}</span>
                    <Checkbox checked={checked} onCheckedChange={(next) => action.mutate({ name: next ? "assign_role" : "remove_role", payload: { role: role.value } })} disabled={role.value === "super_admin" && !isSuperAdmin} />
                  </label>
                );
              })}
              {!canManageAll && <p className="text-sm text-muted-foreground">Company owners manage only company membership roles. Platform roles are hidden.</p>}
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="billing" className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader><CardTitle className="text-base">Package & subscription</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-md border border-border p-4">
                  <div className="text-lg font-semibold">{pkg?.name ?? "No package"}</div>
                  <div className="mt-2"><StatusBadge status={subscription?.status ?? "none"} /></div>
                  <div className="mt-3 text-sm text-muted-foreground">Trial: {formatDate(subscription?.trial_start)} to {formatDate(subscription?.trial_end)}</div>
                  <div className="text-sm text-muted-foreground">Renewal: {formatDate(subscription?.current_period_end)}</div>
                  <div className="text-sm text-muted-foreground">Stripe customer: {target.stripe_customer_id ?? "Not created"}</div>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <Select value={packageId || "none"} onValueChange={(value) => setPackageId(value === "none" ? "" : value)}>
                    <SelectTrigger><SelectValue placeholder="Select package" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Select package</SelectItem>{data.packages.map((item: any) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button onClick={() => action.mutate({ name: "assign_package", payload: { package_id: packageId } })} disabled={!packageId}><PackageCheck className="mr-2 h-4 w-4" />Assign package</Button>
                  <Input type="datetime-local" value={trialEnd} onChange={(e) => setTrialEnd(e.target.value)} />
                  <Button variant="outline" onClick={() => action.mutate({ name: "extend_trial", payload: { trial_end: trialEnd ? new Date(trialEnd).toISOString() : "" } })} disabled={!trialEnd}>Extend trial</Button>
                  <Button variant="outline" onClick={() => action.mutate({ name: "remove_package", payload: {} })}>Remove package</Button>
                  <Button variant="destructive" onClick={() => action.mutate({ name: "suspend_subscription", payload: { reason: "Manual admin suspension" } })}>Suspend access</Button>
                </div>
                <Label>Limit override JSON</Label>
                <Textarea className="font-mono text-xs" value={overrideJson} onChange={(e) => setOverrideJson(e.target.value)} />
                <Button variant="outline" onClick={() => {
                  try {
                    action.mutate({ name: "override_limits", payload: { limits: JSON.parse(overrideJson), reason: "Manual admin override" } });
                  } catch {
                    toast.error("Override JSON is invalid");
                  }
                }}>Save limit overrides</Button>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Usage consumed</CardTitle></CardHeader>
              <CardContent className="space-y-5">
                <UsageLimitBar label="Websites" used={usage.websites_created ?? target.websites_count} limit={pkg?.max_websites} />
                <UsageLimitBar label="Articles" used={usage.articles_created ?? target.articles_count} limit={pkg?.max_articles_per_month} />
                <UsageLimitBar label="AI credits" used={usage.ai_credits_used ?? 0} limit={pkg?.max_ai_credits} />
                <UsageLimitBar label="Media assets" used={usage.media_assets_used ?? target.media_count} limit={pkg?.max_media_assets} />
                <UsageLimitBar label="Storage MB" used={usage.storage_mb_used ?? 0} limit={pkg?.storage_mb} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="websites"><SimpleTable rows={data.websites} columns={["name", "domain", "status", "article_count", "created_at"]} linkBase="/websites" /></TabsContent>
          <TabsContent value="articles"><SimpleTable rows={data.articles} columns={["title", "status", "created_at", "published_at"]} linkBase="/articles" /></TabsContent>
          <TabsContent value="media"><SimpleTable rows={data.media} columns={["file_name", "name", "source", "folder", "created_at"]} /></TabsContent>
          <TabsContent value="invoices"><Invoices invoices={data.invoices} /></TabsContent>
          <TabsContent value="timeline"><Timeline rows={timeline} /></TabsContent>
          <TabsContent value="notes">
            <Card><CardHeader><CardTitle className="text-base">Admin Notes</CardTitle></CardHeader><CardContent className="space-y-4">
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add internal note..." />
              <Button onClick={() => action.mutate({ name: "add_note", payload: { note } })} disabled={!note.trim()}>Add note</Button>
              <div className="space-y-3">{data.notes.map((item: any) => <div key={item.id} className="rounded-md border border-border p-3"><div className="text-sm">{item.note}</div><div className="mt-1 text-xs text-muted-foreground">{formatDate(item.created_at)}</div></div>)}</div>
            </CardContent></Card>
          </TabsContent>
          <TabsContent value="security" className="space-y-4">
            <Card><CardHeader><CardTitle className="text-base">Security</CardTitle></CardHeader><CardContent className="space-y-4">
              {!canImpersonate && <div className="flex items-start gap-2 rounded-md bg-warning/10 p-3 text-sm text-warning"><ShieldAlert className="mt-0.5 h-4 w-4" />Only a Super Admin can impersonate another admin account.</div>}
              <Label>Password reset email</Label>
              <Input
                type="email"
                value={passwordResetEmail}
                onChange={(e) => setPasswordResetEmail(e.target.value)}
                placeholder="user@example.com"
              />
              <Button variant="outline" onClick={() => sendPasswordReset.mutate()} disabled={sendPasswordReset.isPending || !passwordResetEmail.trim()}>
                <KeyRound className="mr-2 h-4 w-4" />
                Send password reset
              </Button>
              <Label>Impersonation reason</Label>
              <Input value={impersonationReason} onChange={(e) => setImpersonationReason(e.target.value)} placeholder="Support investigation, QA, account setup..." />
              <div className="flex gap-2">
                <Button variant="outline" disabled={!canImpersonate} onClick={() => action.mutate({ name: "start_impersonation", payload: { reason: impersonationReason } })}><LogIn className="mr-2 h-4 w-4" />Login as user</Button>
                <Button variant="destructive" onClick={() => action.mutate({ name: "suspend_user", payload: { reason: "Manual admin suspension" } })}>Suspend user</Button>
                <Button variant="outline" onClick={() => action.mutate({ name: "activate_user", payload: {} })}><UserCheck className="mr-2 h-4 w-4" />Activate user</Button>
                <Button variant="outline" onClick={() => setRemoveOpen(true)}><Trash2 className="mr-2 h-4 w-4" />Remove from company/team</Button>
              </div>
            </CardContent></Card>
            <Card className="border-destructive/40">
              <CardHeader>
                <CardTitle className="text-base text-destructive">Danger Zone</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                  Delete user completely removes the Supabase Auth account, profile, memberships, roles, invitations, statuses, cached email references, and user-owned mappings.
                </div>
                {!isSuperAdmin && (
                  <div className="flex items-start gap-2 rounded-md border border-border p-3 text-sm text-muted-foreground">
                    <ShieldAlert className="mt-0.5 h-4 w-4" />
                    Only platform Super Admins can delete users completely.
                  </div>
                )}
                <div className="grid gap-3 md:grid-cols-[1fr_auto]">
                  <Input
                    value={forceDeletePhrase}
                    onChange={(event) => setForceDeletePhrase(event.target.value)}
                    placeholder="Type DELETE"
                    disabled={!isSuperAdmin || forceDeleteUser.isPending}
                  />
                  <Button
                    variant="destructive"
                    onClick={openForceDeleteConfirmation}
                    disabled={!isSuperAdmin || forceDeletePhrase.trim() !== "DELETE" || forceDeleteUser.isPending}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete user completely
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      <AlertDialog open={removeOpen} onOpenChange={(open) => {
        if (!open && !removeUser.isPending) setRemoveOpen(false);
        if (open) setRemoveOpen(true);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from company/team?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes only the selected company/team membership and related open invitations. The Supabase Auth account and profile are kept, so the same email can be re-added and linked again later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="rounded-md border border-border p-3 text-sm">
            {removalImpact.isLoading ? (
              <span className="text-muted-foreground">Checking related data...</span>
            ) : (
              <div className="grid gap-1 text-muted-foreground">
                <span>Company memberships: {removalImpact.data?.impact?.company_memberships?.length ?? 0}</span>
                <span>Roles: {removalImpact.data?.impact?.roles?.length ?? 0}</span>
                <span>Invitations: {removalImpact.data?.impact?.invitations?.length ?? 0}</span>
                <span>Subscriptions: {removalImpact.data?.impact?.subscriptions?.length ?? 0}</span>
                <span>Invoices kept: {removalImpact.data?.impact?.invoices?.length ?? 0}</span>
                <span>Websites kept: {removalImpact.data?.impact?.websites?.length ?? 0}</span>
                <span>Articles kept: {removalImpact.data?.impact?.articles?.length ?? 0}</span>
                <span>Activity logs kept: {removalImpact.data?.impact?.activity_count ?? 0}</span>
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSafeRemove} disabled={!id || removeUser.isPending || removalImpact.isLoading}>
              {removeUser.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Remove from company/team
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={forceDeleteOpen} onOpenChange={(open) => {
        if (!open && !forceDeleteUser.isPending) setForceDeleteOpen(false);
        if (open) setForceDeleteOpen(true);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user completely?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the user from the entire platform and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            This deletes the Supabase Auth account, profile, team memberships, roles, invitations, statuses, cached email references, and user-owned mappings. Type DELETE to unlock the final button.
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={forceDeleteUser.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmForceDelete} disabled={!isSuperAdmin || !id || forceDeletePhrase.trim() !== "DELETE" || forceDeleteUser.isPending}>
              {forceDeleteUser.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Delete user completely
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Summary({ label, value }: { label: string; value: React.ReactNode }) {
  return <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">{label}</p><p className="mt-1 text-lg font-semibold">{value}</p></CardContent></Card>;
}

function SimpleTable({ rows, columns, linkBase }: { rows: any[]; columns: string[]; linkBase?: string }) {
  return (
    <Card><CardContent className="p-0">
      {rows.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">No records found.</div> : (
        <Table><TableHeader><TableRow>{columns.map((column) => <TableHead key={column}>{column.replaceAll("_", " ")}</TableHead>)}{linkBase && <TableHead />}</TableRow></TableHeader><TableBody>
          {rows.map((row) => <TableRow key={row.id}>{columns.map((column) => <TableCell key={column}>{column.includes("date") || column.endsWith("_at") ? formatDate(row[column]) : String(row[column] ?? "—")}</TableCell>)}{linkBase && <TableCell className="text-right"><Button asChild variant="outline" size="sm"><Link to={`${linkBase}/${row.id}`}>Open</Link></Button></TableCell>}</TableRow>)}
        </TableBody></Table>
      )}
    </CardContent></Card>
  );
}

function Invoices({ invoices }: { invoices: any[] }) {
  return (
    <Card><CardContent className="p-0">
      {invoices.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">No invoices found.</div> : (
        <Table><TableHeader><TableRow><TableHead>Invoice</TableHead><TableHead>Package</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>Paid</TableHead><TableHead>Link</TableHead></TableRow></TableHeader><TableBody>
          {invoices.map((invoice) => <TableRow key={invoice.id}><TableCell>{invoice.invoice_number ?? "Pending"}</TableCell><TableCell>{invoice.packages?.name ?? "Unknown"}</TableCell><TableCell>{money(invoice.amount_due || invoice.amount_paid, invoice.currency, true)}</TableCell><TableCell><StatusBadge status={invoice.status} /></TableCell><TableCell>{formatDate(invoice.paid_at)}</TableCell><TableCell>{invoice.hosted_invoice_url ? <a className="text-primary underline" href={invoice.hosted_invoice_url} target="_blank" rel="noreferrer">Open</a> : "—"}</TableCell></TableRow>)}
        </TableBody></Table>
      )}
    </CardContent></Card>
  );
}

function Timeline({ rows }: { rows: any[] }) {
  return (
    <Card><CardContent className="space-y-3 p-4">
      {rows.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">No activity has been recorded.</div> : rows.map((row) => (
        <div key={row.id} className="rounded-md border border-border p-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div><div className="font-medium">{row.message ?? row.event_action ?? row.event_type}</div><div className="text-xs text-muted-foreground">{row.event_type} · {row.event_action ?? "event"} · {formatDate(row.at)}</div></div>
            <StatusBadge status={row.status} />
          </div>
          <div className="mt-2 grid gap-2 text-xs text-muted-foreground md:grid-cols-3">
            <span>Actor: {row.actor_user_id ?? "system"}</span>
            <span>Target: {row.target_user_id ?? "none"}</span>
            <span>Resource: {row.resource_type ?? "none"} {row.resource_id ?? ""}</span>
            <span>IP: {row.ip_address ?? "not captured"}</span>
            <span className="md:col-span-2">User agent: {row.user_agent ?? "not captured"}</span>
          </div>
          <details className="mt-2"><summary className="cursor-pointer text-xs text-primary">Details</summary><pre className="mt-2 max-h-64 overflow-auto rounded-md bg-muted p-3 text-xs">{JSON.stringify(row.metadata ?? {}, null, 2)}</pre></details>
        </div>
      ))}
    </CardContent></Card>
  );
}
